import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link as RouterLink, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import { ThemeProvider } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOffOutlined';
import loginBg from '@/images/login_bg.png';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/i18n/useLocale';
import { createAppTheme } from '@/theme';
import { errorMessageFrom } from '@/lib/toast';

function buildSchema(t: (key: string) => string) {
  return z.object({
    username: z.string().min(1, t('validation.usernameRequired')),
    password: z.string().min(1, t('validation.passwordRequired')),
    rememberMe: z.boolean(),
  });
}

type LoginFormValues = z.infer<ReturnType<typeof buildSchema>>;

// Field/button radii are intentionally rounder than the rest of the app (which uses a flat 2px
// corner everywhere by design) — this page reproduces a fixed marketing mockup, so the rounding
// is scoped to local sx overrides here rather than changing the shared theme tokens.
const FIELD_RADIUS = '10px';
const PILL_RADIUS = '999px';

export default function LoginPage() {
  const { t } = useTranslation('auth');
  const { isAuthenticated, login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { direction } = useLocale();
  // The card is always white per the design mockup, regardless of the app's dark/light toggle —
  // a nested light-mode theme keeps every field/text/icon inside it legible on white instead of
  // hand-overriding each one's color individually.
  const cardTheme = createAppTheme('light', direction);

  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const schema = buildSchema(t);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { username: '', password: '', rememberMe: false },
  });

  if (isAuthenticated) {
    const defaultLanding = user?.clientId ? '/portal/dashboard' : '/dashboard';
    const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? defaultLanding;
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (values: LoginFormValues) => {
    setFormError(null);
    try {
      const loggedInUser = await login(values.username, values.password, values.rememberMe);
      // A Client Portal user has no business landing on the internal /dashboard — send them
      // straight to their own portal shell instead.
      navigate(loggedInUser.clientId ? '/portal/dashboard' : '/dashboard', { replace: true });
    } catch (error) {
      setFormError(errorMessageFrom(error));
    }
  };

  const features = [
    { label: t('features.tracking'), icon: Inventory2OutlinedIcon },
    { label: t('features.analytics'), icon: InsightsOutlinedIcon },
    { label: t('features.management'), icon: AssignmentTurnedInOutlinedIcon },
    { label: t('features.security'), icon: VerifiedUserOutlinedIcon },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        backgroundImage: `url(${loginBg})`,
        backgroundSize: '100% 100%',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'flex-end',
          flex: 1,
          minHeight: '100vh',
          p: { md: 6, lg: 8 },
          pb: { md: 7, lg: 9 },
        }}
      >
        <Stack spacing={0.5} sx={{ maxWidth: 440, mb: 5 }}>
          <Typography variant="h2" sx={{ fontWeight: 500, color: '#1a2540', lineHeight: 1.3 }}>
            {t('tagline.line1Plain')}{' '}
            <Box component="span" sx={{ fontWeight: 800, color: 'primary.main' }}>
              {t('tagline.line1Bold')}
            </Box>
          </Typography>
          <Typography variant="h2" sx={{ fontWeight: 500, color: '#1a2540', lineHeight: 1.3 }}>
            {t('tagline.line2Plain')}{' '}
            <Box component="span" sx={{ fontWeight: 800, color: 'primary.main' }}>
              {t('tagline.line2Bold')}
            </Box>
          </Typography>
          <Typography variant="h2" sx={{ fontWeight: 500, color: '#1a2540', lineHeight: 1.3 }}>
            {t('tagline.line3Plain')}{' '}
            <Box component="span" sx={{ fontWeight: 800, color: 'primary.main' }}>
              {t('tagline.line3Bold')}
            </Box>
          </Typography>
          <Typography variant="body1" sx={{ color: '#5b6472', mt: 2 }}>
            {t('tagline.subtitleLine1')}
            <br />
            {t('tagline.subtitleLine2')}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={3}>
          {features.map((f) => (
            <Stack key={f.label} spacing={1} alignItems="center" sx={{ width: 96 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '16px',
                  bgcolor: 'rgba(255,255,255,0.9)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 14px rgba(15,23,42,0.1)',
                }}
              >
                <f.icon sx={{ color: 'primary.main' }} />
              </Box>
              <Typography variant="caption" align="center" sx={{ color: '#3d4a5c', fontWeight: 600, lineHeight: 1.3 }}>
                {f.label}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          bgcolor: { xs: 'rgba(255,255,255,0.85)', md: 'transparent' },
        }}
      >
        <ThemeProvider theme={cardTheme}>
          <Paper elevation={8} sx={{ width: '100%', maxWidth: 400, borderRadius: '20px', p: 5, bgcolor: '#fff' }}>
            <Stack alignItems="center" spacing={0.75} sx={{ mb: 3 }}>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {t('title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('subtitle')}
              </Typography>
              <Box sx={{ width: 40, height: 3, borderRadius: '2px', bgcolor: 'primary.main', mt: 1 }} />
            </Stack>

            <Box component="form" onSubmit={(e) => void handleSubmit(onSubmit)(e)} noValidate>
              <Stack spacing={2.5}>
                <Controller
                  name="username"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      placeholder={t('username')}
                      error={Boolean(errors.username)}
                      helperText={errors.username?.message}
                      fullWidth
                      autoFocus
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonOutlineIcon fontSize="small" color="action" />
                            </InputAdornment>
                          ),
                          sx: { borderRadius: FIELD_RADIUS },
                        },
                      }}
                    />
                  )}
                />

                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type={showPassword ? 'text' : 'password'}
                      placeholder={t('password')}
                      error={Boolean(errors.password)}
                      helperText={errors.password?.message}
                      fullWidth
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockOutlinedIcon fontSize="small" color="action" />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowPassword((v) => !v)} edge="end" size="small">
                                {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                              </IconButton>
                            </InputAdornment>
                          ),
                          sx: { borderRadius: FIELD_RADIUS },
                        },
                      }}
                    />
                  )}
                />

                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Controller
                    name="rememberMe"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Checkbox {...field} checked={field.value} size="small" />}
                        label={<Typography variant="body2">{t('rememberMe')}</Typography>}
                      />
                    )}
                  />
                  <Link component={RouterLink} to="/forgot-password" variant="body2" underline="hover">
                    {t('forgotPassword')}
                  </Link>
                </Stack>

                {formError ? (
                  <Typography variant="body2" color="error">
                    {formError}
                  </Typography>
                ) : undefined}

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isSubmitting}
                  sx={{ borderRadius: PILL_RADIUS, py: 1.25 }}
                >
                  {isSubmitting ? t('authenticating') : t('submit')}
                </Button>
              </Stack>
            </Box>

            <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.75} sx={{ mt: 4 }}>
              <GppGoodOutlinedIcon fontSize="small" color="disabled" />
              <Typography variant="body2" color="text.secondary" align="center">
                {t('footer')}
              </Typography>
            </Stack>
          </Paper>
        </ThemeProvider>
      </Box>
    </Box>
  );
}
